import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  Checkbox,
  Group,
  Paper,
  Stack,
  Table,
  Text,
  UnstyledButton,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { ChevronDown, ChevronUp, ChevronsUpDown, GripVertical } from 'lucide-react';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DraggableAttributes,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

export type SortDirection = 'asc' | 'desc';

export interface ResponsiveListColumn<T> {
  key: string;
  label: string;
  render: (row: T) => ReactNode;
  hideOnMobile?: boolean;
  /**
   * When set (and the list is given an `onSort` handler), this column's header
   * becomes a clickable sort control. The value is the sort key reported back
   * to `onSort` and compared against `sortBy` to show the active indicator.
   */
  sortKey?: string;
  /**
   * Desktop only. Caps the column so the browser's auto table layout can't hand
   * all free space to whichever column has the longest content.
   */
  width?: number | string;
  /**
   * Desktop only. Marks this column as the one that absorbs leftover width.
   * Switches the whole table to `table-layout: fixed`, so every other column
   * holds its declared `width` exactly and this one takes the remainder — the
   * only reliable way to place the slack, since auto layout honours neither
   * `width` nor `maxWidth` when it spreads a 100%-width table's spare pixels.
   * Give every other column a `width`, or fixed layout will divide the
   * remainder between them. This column's own `width` is its floor, enforced
   * by scrolling. Set on at most one column, and let its cell wrap.
   */
  grow?: boolean;
  /** Desktop only. Keeps the cell on one line (dates, badges, action rows). */
  noWrap?: boolean;
  /**
   * Native hover tooltip for the cell, so a value clipped by `width`/`noWrap`
   * is still readable. Defaults to the rendered value when it's a plain string.
   */
  title?: (row: T) => string;
}

interface ResponsiveListProps<T> {
  columns: ResponsiveListColumn<T>[];
  data: T[];
  keyField: keyof T;
  onRowClick?: (row: T) => void;
  emptyMessage?: string;
  /**
   * Opt-in: when true, renders a checkbox column for multi-select and a
   * trailing actions cell (`rowActions`). When false/undefined, both are
   * hidden and the component behaves exactly as before.
   */
  selectable?: boolean;
  /** Currently-selected row ids. Owned by the parent so selection can persist across pages. */
  selectedIds?: Set<string>;
  /** Called with the next selection set whenever the user toggles a row or the header checkbox. */
  onSelectionChange?: (selectedIds: Set<string>) => void;
  /** Per-row action slot (e.g. a delete button). Only rendered when `selectable` is true. */
  rowActions?: (row: T) => ReactNode;
  /**
   * Optional predicate used with `selectable`. Only rows returning true get a
   * checkbox and count toward the header "select all"; other rows show an empty
   * cell and can't be selected. When omitted, every row is selectable.
   */
  isRowSelectable?: (row: T) => boolean;
  /** Active sort key (matches a column's `sortKey`), or null/undefined when unsorted. */
  sortBy?: string | null;
  /** Active sort direction. Ignored when `sortBy` is null. */
  sortDir?: SortDirection;
  /**
   * Called when a sortable column header is clicked. The component owns the
   * asc → desc → off cycle and reports the resulting state; consumers just
   * store it. `sortBy` is null when the cycle returns to the default order.
   */
  onSort?: (sortBy: string | null, sortDir: SortDirection) => void;
  /**
   * Opt-in: when true (desktop only), renders a drag-handle column and lets
   * the user reorder rows via drag-and-drop. Requires `onReorder`. Default-off
   * keeps all existing usages unchanged. Touch/mobile drag is out of scope —
   * drag is disabled when the list renders its mobile card layout.
   */
  draggable?: boolean;
  /**
   * Called after a successful drag-drop with the full list of row ids in their
   * new order. The parent is responsible for persisting the new order.
   */
  onReorder?: (orderedIds: string[]) => void;
  /**
   * Optional predicate used with `draggable`. Only rows returning true expose a
   * drag handle and act as drop targets; other rows are pinned in place. When
   * omitted, every row is draggable.
   */
  canDragRow?: (row: T) => boolean;
  /**
   * Keep the header row pinned while the list scrolls inside a capped-height
   * container. Only meaningful when the parent wraps the list in a scroll area.
   */
  stickyHeader?: boolean;
  /**
   * Opt-in: switch to the mobile card layout when the list's own wrapper
   * (not the viewport) measures narrower than this width, via
   * `ResizeObserver`. Lets a container that shrinks for a reason other than
   * viewport size — a collapsible sidebar, a split pane — trigger the card
   * fallback on its own. When unset, keeps the existing
   * `useMediaQuery('(max-width: 768px)')` viewport check.
   */
  switchToCardsBelow?: number;
  /**
   * Opt-in: render the desktop table at a fixed `table-layout: fixed`,
   * `width: 100%` instead of wrapping it in a `Table.ScrollContainer` with a
   * `minWidth` floor. Every column's declared `width` is then authoritative
   * except a `grow` column, which absorbs (or gives up) whatever's left —
   * so the table always fills its container and never scrolls horizontally.
   * Default/unset keeps today's scroll-floor behavior.
   */
  fitContainer?: boolean;
  /**
   * Opt-in: render the mobile cards flat — no per-card shadow, border, radius
   * or gap — separated by a single divider line, like table rows. For lists
   * whose own wrapper already draws a border (a bordered `Paper`), so the card
   * borders don't double up with the container's and the elevation doesn't
   * clash with the flat table it replaces. Default/unset keeps the elevated,
   * bordered card, which suits a shadow-only (borderless) container.
   */
  flatCards?: boolean;
}

interface SortableRowProps {
  id: string;
  onClick?: () => void;
  children: (handleProps: {
    // dnd-kit's own nominal type — it has no index signature, so a structural
    // `Record<string, unknown>` here would reject what `useSortable` returns.
    attributes: DraggableAttributes;
    listeners: Record<string, unknown> | undefined;
  }) => ReactNode;
}

// A single draggable table row. Exposes drag-handle props to its children so
// only the handle cell starts a drag (the rest of the row stays clickable).
function SortableRow({ id, onClick, children }: SortableRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
  } = useSortable({ id });

  return (
    <Table.Tr
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        cursor: onClick ? 'pointer' : undefined,
        opacity: isDragging ? 0.5 : 1,
        // Drop indicator (AC #3): a line at the top of the row being dragged over.
        boxShadow: isOver
          ? 'inset 0 2px 0 0 var(--mantine-color-blue-6)'
          : undefined,
        position: 'relative',
        zIndex: isDragging ? 1 : undefined,
      }}
      onClick={onClick}
    >
      {children({ attributes, listeners })}
    </Table.Tr>
  );
}

export function ResponsiveList<T>({
  columns,
  data,
  keyField,
  onRowClick,
  emptyMessage = 'No records found',
  selectable = false,
  selectedIds,
  onSelectionChange,
  rowActions,
  isRowSelectable,
  sortBy,
  sortDir = 'asc',
  onSort,
  draggable = false,
  onReorder,
  canDragRow,
  stickyHeader = false,
  switchToCardsBelow,
  fitContainer = false,
  flatCards = false,
}: ResponsiveListProps<T>) {
  const viewportMobile = useMediaQuery('(max-width: 768px)');

  // Container-width card switch (opt-in). Measures the wrapper itself rather
  // than the viewport, so a container that narrows for a reason other than
  // screen size (a collapsible sidebar) can flip to cards on its own.
  // `containerWidth` starts null (nothing measured yet); fall back to the
  // viewport query for that first render so there's no flash of the wrong
  // layout while the observer spins up.
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);

  useEffect(() => {
    if (switchToCardsBelow === undefined) return;
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width;
      if (width !== undefined) setContainerWidth(width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [switchToCardsBelow]);

  const isMobile =
    switchToCardsBelow === undefined
      ? viewportMobile
      : containerWidth === null
        ? viewportMobile
        : containerWidth < switchToCardsBelow;

  // Only wraps in a measuring div when the container-switch is opted into —
  // default (no props) usages get no DOM change at all.
  const wrap = (node: ReactNode): ReactNode =>
    switchToCardsBelow === undefined ? node : <div ref={containerRef}>{node}</div>;

  // Drag is desktop-only (touch out of scope per AC) and requires a handler.
  const dragEnabled = draggable && !isMobile && !!onReorder;
  const isRowDraggable = (row: T) => !canDragRow || canDragRow(row);

  // Require a small pointer movement before a drag starts so plain clicks
  // (row navigation) still fire normally.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !onReorder) return;
    const ids = data.map((row) => String(row[keyField]));
    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;
    onReorder(arrayMove(ids, oldIndex, newIndex));
  };

  // asc → desc → off cycle. Clicking a new column starts at asc; clicking the
  // active column flips to desc, then clears back to the default order.
  const handleSort = (sortKey: string) => {
    if (!onSort) return;
    if (sortBy !== sortKey) onSort(sortKey, 'asc');
    else if (sortDir === 'asc') onSort(sortKey, 'desc');
    else onSort(null, 'asc');
  };

  const rowId = (row: T) => String(row[keyField]);
  const selected = selectedIds ?? new Set<string>();
  const canSelectRow = (row: T) => !isRowSelectable || isRowSelectable(row);

  // "Select all" toggles the current page only; selections on other pages
  // (held by the parent) are left untouched. Only rows that pass
  // `isRowSelectable` participate.
  const pageIds = data.filter(canSelectRow).map(rowId);
  const allOnPageSelected =
    pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  const someOnPageSelected =
    pageIds.some((id) => selected.has(id)) && !allOnPageSelected;

  const toggleRow = (id: string) => {
    if (!onSelectionChange) return;
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onSelectionChange(next);
  };

  const toggleAllOnPage = () => {
    if (!onSelectionChange) return;
    const next = new Set(selected);
    if (allOnPageSelected) {
      pageIds.forEach((id) => next.delete(id));
    } else {
      pageIds.forEach((id) => next.add(id));
    }
    onSelectionChange(next);
  };

  if (data.length === 0) {
    return wrap(
      <Text ta="center" c="dimmed" py="lg">
        {emptyMessage}
      </Text>
    );
  }

  if (isMobile) {
    return wrap(
      <Stack gap={flatCards ? 0 : 'sm'}>
        {data.map((row, index) => {
          const mobileColumns = columns.filter((c) => !c.hideOnMobile);
          const [first, ...rest] = mobileColumns;
          const id = rowId(row);
          // Flat cards read like table rows: no shadow/border/radius, a single
          // divider between them (none under the last), framed by the caller's
          // own bordered container instead of stacking a second border on top.
          const flatStyle = {
            ...(index < data.length - 1
              ? { borderBottom: '1px solid var(--mantine-color-default-border)' }
              : {}),
            ...(onRowClick ? { cursor: 'pointer' } : {}),
          };
          return (
            <Paper
              key={id}
              p="sm"
              shadow={flatCards ? undefined : 'xs'}
              radius={flatCards ? 0 : 'sm'}
              withBorder={!flatCards}
              style={flatCards ? flatStyle : (onRowClick ? { cursor: 'pointer' } : undefined)}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
            >
              <Stack gap={6}>
                <Group justify="space-between" wrap="nowrap" gap="xs">
                  <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
                    {selectable && canSelectRow(row) && (
                      <Checkbox
                        checked={selected.has(id)}
                        onChange={() => toggleRow(id)}
                        onClick={(e) => e.stopPropagation()}
                        aria-label="Select row"
                      />
                    )}
                    <Text fw={600} size="sm">
                      {first.render(row)}
                    </Text>
                  </Group>
                  {selectable && rowActions && (
                    <div onClick={(e) => e.stopPropagation()}>
                      {rowActions(row)}
                    </div>
                  )}
                </Group>
                {rest.map((col) => (
                  <Group key={col.key} justify="space-between" wrap="nowrap" gap="xs">
                    <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
                      {col.label}
                    </Text>
                    <Text size="sm" ta="right" style={{ minWidth: 0 }}>
                      {col.render(row)}
                    </Text>
                  </Group>
                ))}
              </Stack>
            </Paper>
          );
        })}
      </Stack>
    );
  }

  // A grow column, or `fitContainer`, both need every column's declared width
  // to be authoritative, so both switch the table to fixed layout. Under auto
  // layout neither `width` nor `maxWidth` binds: the browser spreads a
  // 100%-width table's leftover pixels across the columns whatever their caps
  // say, which is how one column ends up as a dead gap. Fixed layout makes the
  // px widths authoritative and hands the whole remainder to the one column
  // that declares none — so `grow` emits no width at all.
  const growing = columns.some((col) => col.grow);
  const useFixedLayout = growing || fitContainer;

  const cellStyle = (col: ResponsiveListColumn<T>) => {
    if (col.grow) return undefined;
    return col.width || col.noWrap
      ? {
          width: col.width,
          maxWidth: col.width,
          // nowrap without a clip just spills into the next column.
          ...(col.noWrap && {
            whiteSpace: 'nowrap' as const,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }),
        }
      : undefined;
  };

  // Fixed layout would otherwise crush the grow column on a narrow viewport —
  // its declared `width` is the floor, honoured by scrolling past it instead.
  // `fitContainer` opts out of the floor entirely: the table stays at 100% of
  // its container (see below) and the grow column shrinks with it instead,
  // so the table can never need to scroll horizontally.
  const minTableWidth = growing && !fitContainer
    ? columns.reduce((sum, col) => sum + (typeof col.width === 'number' ? col.width : 0), 0)
    : undefined;

  // Renders the (non-handle) cells for a single row, shared by the draggable
  // and static row paths.
  const renderRowCells = (row: T, id: string) => (
    <>
      {selectable && (
        <Table.Td onClick={(e) => e.stopPropagation()}>
          {canSelectRow(row) && (
            <Checkbox
              checked={selected.has(id)}
              onChange={() => toggleRow(id)}
              aria-label="Select row"
            />
          )}
        </Table.Td>
      )}
      {columns.map((col) => {
        const content = col.render(row);
        return (
          <Table.Td
            key={col.key}
            style={cellStyle(col)}
            title={col.title?.(row) ?? (typeof content === 'string' ? content : undefined)}
          >
            {content}
          </Table.Td>
        );
      })}
      {selectable && rowActions && (
        <Table.Td onClick={(e) => e.stopPropagation()}>
          {rowActions(row)}
        </Table.Td>
      )}
    </>
  );

  const tableBody = (
    <Table.Tbody>
      {data.map((row) => {
        const id = rowId(row);
        if (dragEnabled && isRowDraggable(row)) {
          return (
            <SortableRow
              key={id}
              id={id}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
            >
              {({ attributes, listeners }) => (
                <>
                  <Table.Td
                    onClick={(e) => e.stopPropagation()}
                    style={{ width: 36, cursor: 'grab', touchAction: 'none' }}
                    {...attributes}
                    {...listeners}
                    aria-label="Drag to reorder"
                  >
                    <GripVertical size={16} opacity={0.5} />
                  </Table.Td>
                  {renderRowCells(row, id)}
                </>
              )}
            </SortableRow>
          );
        }
        return (
          <Table.Tr
            key={id}
            style={onRowClick ? { cursor: 'pointer' } : undefined}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
          >
            {/* Keep an empty handle cell so pinned rows stay column-aligned. */}
            {dragEnabled && <Table.Td style={{ width: 36 }} />}
            {renderRowCells(row, id)}
          </Table.Tr>
        );
      })}
    </Table.Tbody>
  );

  const table = (
    <Table
      striped
      highlightOnHover
      stickyHeader={stickyHeader}
      style={
        useFixedLayout
          ? { tableLayout: 'fixed', ...(fitContainer ? { width: '100%' } : {}) }
          : undefined
      }
    >
      <Table.Thead>
        <Table.Tr>
          {dragEnabled && <Table.Th style={{ width: 36 }} />}
          {selectable && (
            <Table.Th style={{ width: 40 }}>
              <Checkbox
                checked={allOnPageSelected}
                indeterminate={someOnPageSelected}
                onChange={toggleAllOnPage}
                aria-label="Select all rows on this page"
              />
            </Table.Th>
          )}
          {columns.map((col) => {
            const sortable = !!col.sortKey && !!onSort;
            if (!sortable) {
              return <Table.Th key={col.key} style={cellStyle(col)}>{col.label}</Table.Th>;
            }
            const active = sortBy === col.sortKey;
            return (
              // aria-sort on the header cell is what tells a screen reader which
              // column the list is ordered by and which way — the chevron alone is
              // invisible to it, and the button's label only says what a click does.
              <Table.Th
                key={col.key}
                style={cellStyle(col)}
                aria-sort={
                  active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'
                }
              >
                <UnstyledButton
                  onClick={() => handleSort(col.sortKey as string)}
                  aria-label={`Sort by ${col.label}`}
                  style={{ display: 'inline-flex' }}
                >
                  <Group gap={4} wrap="nowrap">
                    <Text size="sm" fw={700} inherit>
                      {col.label}
                    </Text>
                    {active ? (
                      sortDir === 'asc' ? (
                        <ChevronUp size={14} />
                      ) : (
                        <ChevronDown size={14} />
                      )
                    ) : (
                      <ChevronsUpDown size={14} opacity={0.4} />
                    )}
                  </Group>
                </UnstyledButton>
              </Table.Th>
            );
          })}
          {selectable && rowActions && (
            <Table.Th style={{ width: 60 }} />
          )}
        </Table.Tr>
      </Table.Thead>
      {tableBody}
    </Table>
  );

  const scrolled = minTableWidth ? (
    // Native overflow, not ScrollArea — ScrollArea's viewport wrapper breaks
    // the sticky header these lists use.
    <Table.ScrollContainer minWidth={minTableWidth} type="native">
      {table}
    </Table.ScrollContainer>
  ) : (
    table
  );

  if (!dragEnabled) return wrap(scrolled);

  return wrap(
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={data
          .filter((row) => isRowDraggable(row))
          .map((row) => String(row[keyField]))}
        strategy={verticalListSortingStrategy}
      >
        {scrolled}
      </SortableContext>
    </DndContext>
  );
}
